import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

/**
 * Extracts a JSON object or array assigned to a JavaScript variable without executing the script.
 *
 * @param {string} source
 * @param {string} variableName
 * @returns {Record<string, any> | Array<any> | null}
 */
function extractAssignedJson(source, variableName) {
  const escapedVariableName = variableName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const assignment = new RegExp(`${escapedVariableName}\\s*=`).exec(source);
  if (!assignment) return null;

  let start = assignment.index + assignment[0].length;
  while (/\s/.test(source[start] || '')) start += 1;

  const openingCharacter = source[start];
  if (openingCharacter !== '{' && openingCharacter !== '[') return null;

  const closingCharacter = openingCharacter === '{' ? '}' : ']';
  let depth = 0;
  let insideString = false;
  let escaped = false;

  for (let index = start; index < source.length; index += 1) {
    const character = source[index];

    if (insideString) {
      if (escaped) {
        escaped = false;
      } else if (character === '\\') {
        escaped = true;
      } else if (character === '"') {
        insideString = false;
      }
      continue;
    }

    if (character === '"') {
      insideString = true;
    } else if (character === openingCharacter) {
      depth += 1;
    } else if (character === closingCharacter) {
      depth -= 1;

      if (depth === 0) {
        try {
          return JSON.parse(source.slice(start, index + 1));
        } catch (_error) {
          return null;
        }
      }
    }
  }

  return null;
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {string | null} */
  #returnUrl = null;
  /** @type {{ product: any, data: any } | null} */
  #previousVoState = null;
  /** @type {boolean} */
  #reloadAfterVoDialog = false;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#addVoProductOptions(productGrid, html);
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();

    if (productGrid) {
      await this.#initializeVoProductOptions();
    }
  };

  /**
   * Adds VO's product configuration to the content that is moved into the quick-add modal.
   * Older product templates can render the app block outside data-product-grid-content, so keep a fallback clone.
   *
   * @param {Element} productGrid
   * @param {Document} html
   */
  #addVoProductOptions(productGrid, html) {
    const existingVoBlock = productGrid.querySelector('.best-custom-product-options');
    const voBlock = existingVoBlock || html.querySelector('.best-custom-product-options');
    const voScript = Array.from(html.scripts).find((script) => script.textContent?.includes('var bcpo_product='));
    if (!voBlock || !voScript?.textContent) return;

    const product = extractAssignedJson(voScript.textContent, 'bcpo_product');
    if (!product || Array.isArray(product)) return;

    const optionsWithValues = extractAssignedJson(voScript.textContent, 'bcpo_product.options_with_values');
    if (Array.isArray(optionsWithValues)) {
      product.options_with_values = optionsWithValues;
    }

    const data = extractAssignedJson(voScript.textContent, 'bcpo_data');
    if (!data || !Array.isArray(data.virtual_options) || data.virtual_options.length === 0) return;

    if (!existingVoBlock) {
      const voContainer = /** @type {Element} */ (voBlock.cloneNode(true));
      const buyButtons = productGrid.querySelector('.buy-buttons-block');
      const productDetails = productGrid.querySelector('.product-details .group-block');

      if (buyButtons) {
        buyButtons.before(voContainer);
      } else {
        productDetails?.append(voContainer);
      }
    }

    const configuration = document.createElement('script');
    configuration.type = 'application/json';
    configuration.dataset.voProductOptions = '';
    configuration.textContent = JSON.stringify({ product, data });
    productGrid.append(configuration);
  }

  /**
   * Initializes VO against the product form inside the open quick-add dialog.
   */
  async #initializeVoProductOptions() {
    const modalContent = document.getElementById('quick-add-modal-content');
    const configuration = modalContent?.querySelector('script[data-vo-product-options]');
    const form = modalContent?.querySelector('form[data-type="add-to-cart-form"]');
    const voContainer = modalContent?.querySelector('.best-custom-product-options');
    if (!configuration?.textContent || !(form instanceof HTMLFormElement) || !voContainer) return;

    let voConfiguration;
    try {
      voConfiguration = JSON.parse(configuration.textContent);
    } catch (_error) {
      return;
    }

    if (!voConfiguration?.product) return;

    const voRuntime = await this.#waitForVoRuntime();
    if (!voRuntime) return;

    this.#returnUrl ||= window.location.href;
    this.#previousVoState ||= {
      product: window.bcpo_product,
      data: window.bcpo_data,
    };

    window.history.replaceState(window.history.state, '', this.productPageUrl);
    window.bcpo_product = voConfiguration.product;
    window.bcpo_data = voConfiguration.data || {};
    this.#reloadAfterVoDialog = true;

    try {
      voRuntime.initialize(
        voConfiguration.product,
        window.bcpo_settings || {},
        voConfiguration.data || {},
        voRuntime.$(form),
        null,
      );
    } catch (error) {
      console.error('Unable to initialize VO Product Options in quick add', error);
      this.#restoreVoProductContext();
    }
  }

  /**
   * Waits briefly for VO's deferred app embed to finish loading.
   *
   * @returns {Promise<{ initialize: Function, $: Function } | null>}
   */
  async #waitForVoRuntime() {
    const timeout = 3000;
    const interval = 50;
    let elapsed = 0;

    while (elapsed < timeout) {
      if (typeof window.bcpoFunction === 'function' && typeof window.bcpo?.$ === 'function') {
        return { initialize: window.bcpoFunction, $: window.bcpo.$ };
      }

      await new Promise((resolve) => setTimeout(resolve, interval));
      elapsed += interval;
    }

    return null;
  }
  #restoreVoProductContext() {
    const shouldReload = this.#reloadAfterVoDialog;
    this.#reloadAfterVoDialog = false;

    if (this.#returnUrl) {
      window.history.replaceState(window.history.state, '', this.#returnUrl);
      this.#returnUrl = null;
    }

    if (this.#previousVoState) {
      window.bcpo_product = this.#previousVoState.product;
      window.bcpo_data = this.#previousVoState.data;
      this.#previousVoState = null;
    }

    if (shouldReload) window.location.reload();
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        this.toggleAttribute('stay-visible', false);
        this.#restoreVoProductContext();
      },
      { once: true },
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const voProductOptions = productGrid.querySelector('.best-custom-product-options');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !productPrice) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      if (variantPicker) productGrid.appendChild(variantPicker);
      if (voProductOptions) productGrid.appendChild(voProductOptions);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
